import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './S_AddCapsuleContentPopup.module.css';
import { M_FileProps } from '@/components/M_Molecules/M_File/M_File';
import FileViewer from './FileViewer';
import UploadView from './UploadView';
import { useTranslation } from '@/hooks/useTranslation';
import { useIsMobile } from '@/hooks/useIsMobile';
import cn from 'classnames';
import { uploadsApi } from '@/services/uploadsApi';
import { getMediaUrl } from '@/utils/mediaUrl';

interface S_AddCapsuleContentPopupProps {
  setIsAddCapsuleContentPopupOpen: (isOpen: boolean) => void;
  onSaveContent?: (contentItem: any) => void;
}

export default function S_AddCapsuleContentPopup({
  setIsAddCapsuleContentPopupOpen,
  onSaveContent,
}: S_AddCapsuleContentPopupProps) {
  const t = useTranslation();
  const isMobile = useIsMobile(1080);
  const [files, setFiles] = useState<M_FileProps[]>([]);
  const [originalFiles, setOriginalFiles] = useState<File[]>([]);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null
  );
  const [fileDescriptions, setFileDescriptions] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);

  const handleClosePopup = () => {
    setIsAddCapsuleContentPopupOpen(false);
  };

  const handleFilesUploaded = (fileList: FileList) => {
    const fileArray = Array.from(fileList);
    const newFiles: M_FileProps[] = [];
    const maxTotalFiles = 18;

    fileArray.forEach(file => {
      const formatRaw = file.type.split('/')[1]?.toUpperCase() ?? '';
      const format =
        file.type === 'video/quicktime'
          ? ('MOV' as M_FileProps['format'])
          : (formatRaw as M_FileProps['format']);
      const size = file.size;
      const imageUrl = URL.createObjectURL(file);

      let mediaType: 'image' | 'video' | 'audio' | 'gif' = 'image';
      if (file.type.startsWith('video/')) {
        mediaType = 'video';
      } else if (file.type.startsWith('audio/')) {
        mediaType = 'audio';
      } else if (file.type === 'image/gif') {
        mediaType = 'gif';
      }

      newFiles.push({
        image: imageUrl,
        format: format,
        size: size,
        mediaType: mediaType,
        hasDescription: false,
        onOpen: () => {},
        onDelete: () => {},
      });
    });

    const totalFiles = files.length + newFiles.length;
    if (totalFiles > maxTotalFiles) {
      const remainingSlots = maxTotalFiles - files.length;
      if (remainingSlots > 0) {
        const limitedNewFiles = newFiles.slice(0, remainingSlots);
        const limitedFiles = fileArray.slice(0, remainingSlots);
        setOriginalFiles(prev => [...prev, ...limitedFiles]);
        setFiles(prev => [...prev, ...limitedNewFiles]);
        alert(
          t('capsuleCreate.contentUpload.maxFilesPartial')
            .replace('{{max}}', String(maxTotalFiles))
            .replace('{{loaded}}', String(limitedNewFiles.length))
            .replace('{{total}}', String(newFiles.length))
        );
      } else {
        alert(
          t('capsuleCreate.contentUpload.maxFilesReached').replace(
            '{{max}}',
            String(maxTotalFiles)
          )
        );
      }
    } else {
      setOriginalFiles(prev => [...prev, ...fileArray]);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleClearAll = () => {
    files.forEach(file => URL.revokeObjectURL(file.image));
    setFiles([]);
    setOriginalFiles([]);
    setFileDescriptions([]);
  };

  const handleDeleteFile = (indexToDelete: number) => {
    setFiles(prevFiles => {
      const fileToDelete = prevFiles[indexToDelete];
      URL.revokeObjectURL(fileToDelete.image);
      return prevFiles.filter((_, index) => index !== indexToDelete);
    });

    setOriginalFiles(prev =>
      prev.filter((_, index) => index !== indexToDelete)
    );

    setFileDescriptions(prev => {
      const newDescriptions = [...prev];
      newDescriptions.splice(indexToDelete, 1);
      return newDescriptions;
    });
  };

  const handleOpenFile = (index: number) => {
    setIsViewMode(true);
    setSelectedFileIndex(index);
    setFileDescriptions(prev => {
      const newDescriptions = [...prev];
      while (newDescriptions.length <= index) {
        newDescriptions.push('');
      }
      return newDescriptions;
    });
    setFiles(prevFiles => {
      const newFiles = [...prevFiles];
      if (newFiles[index]) {
        const description = fileDescriptions[index] || '';
        newFiles[index] = {
          ...newFiles[index],
          hasDescription: description.trim().length > 0,
        };
      }
      return newFiles;
    });
  };

  const handleBackToUpload = () => {
    setIsViewMode(false);
    setSelectedFileIndex(null);
  };

  const handleDescriptionChange = (description: string) => {
    if (selectedFileIndex !== null) {
      setFileDescriptions(prev => {
        const newDescriptions = [...prev];
        newDescriptions[selectedFileIndex] = description;
        return newDescriptions;
      });
      setFiles(prevFiles => {
        const newFiles = [...prevFiles];
        if (newFiles[selectedFileIndex]) {
          newFiles[selectedFileIndex] = {
            ...newFiles[selectedFileIndex],
            hasDescription: description.trim().length > 0,
          };
        }
        return newFiles;
      });
    }
  };

  const handleDeleteDescription = () => {
    if (selectedFileIndex !== null) {
      setFileDescriptions(prev => {
        const newDescriptions = [...prev];
        newDescriptions[selectedFileIndex] = '';
        return newDescriptions;
      });
      setFiles(prevFiles => {
        const newFiles = [...prevFiles];
        if (newFiles[selectedFileIndex]) {
          newFiles[selectedFileIndex] = {
            ...newFiles[selectedFileIndex],
            hasDescription: false,
          };
        }
        return newFiles;
      });
    }
  };

  const validateFile = (file: File): string | null => {
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
      return t('capsuleCreate.contentUpload.fileTooLarge')
        .replace('{{fileName}}', file.name)
        .replace('{{size}}', sizeInMB)
        .replace('{{maxSize}}', '20');
    }

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'audio/mpeg',
      'video/mp4',
      'video/quicktime',
    ];

    if (!allowedTypes.includes(file.type)) {
      const allowedFormats = t('capsuleCreate.contentUpload.allowedFormats');
      return t('capsuleCreate.contentUpload.unsupportedFormat')
        .replace('{{fileName}}', file.name)
        .replace('{{formats}}', allowedFormats);
    }

    return null;
  };

  const handleReplaceMedia = () => {
    if (selectedFileIndex === null) return;
    replaceFileInputRef.current?.click();
  };

  const handleReplaceFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0 || selectedFileIndex === null) {
      return;
    }

    const file = fileList[0];
    const error = validateFile(file);
    if (error) {
      alert(error);
      e.target.value = '';
      return;
    }

    const formatRaw = file.type.split('/')[1]?.toUpperCase() ?? '';
    const format =
      file.type === 'video/quicktime'
        ? ('MOV' as M_FileProps['format'])
        : (formatRaw as M_FileProps['format']);
    const size = file.size;
    const imageUrl = URL.createObjectURL(file);

    let mediaType: 'image' | 'video' | 'audio' | 'gif' = 'image';
    if (file.type.startsWith('video/')) {
      mediaType = 'video';
    } else if (file.type.startsWith('audio/')) {
      mediaType = 'audio';
    } else if (file.type === 'image/gif') {
      mediaType = 'gif';
    }

    setFiles(prevFiles => {
      const newFiles = [...prevFiles];
      const oldFile = newFiles[selectedFileIndex];
      URL.revokeObjectURL(oldFile.image);
      const hasDescription = fileDescriptions[selectedFileIndex]
        ? fileDescriptions[selectedFileIndex].trim().length > 0
        : false;
      newFiles[selectedFileIndex] = {
        image: imageUrl,
        format: format,
        size: size,
        mediaType: mediaType,
        hasDescription: hasDescription,
        onOpen: () => {},
        onDelete: () => {},
      };
      return newFiles;
    });

    setOriginalFiles(prev => {
      const newFiles = [...prev];
      newFiles[selectedFileIndex] = file;
      return newFiles;
    });

    e.target.value = '';
  };

  const handleSave = async () => {
    if (onSaveContent && files.length > 0 && originalFiles.length > 0) {
      setIsUploading(true);
      try {
        const filesSnapshot = [...files];
        const descriptionsSnapshot = [...fileDescriptions];
        const originalFilesSnapshot = [...originalFiles];

        const minLength = Math.min(
          filesSnapshot.length,
          originalFilesSnapshot.length
        );

        const filesToUpload = originalFilesSnapshot.slice(0, minLength);
        const uploadResults = await Promise.all(
          filesToUpload.map(file => uploadsApi.uploadFile(file))
        );

        uploadResults.forEach((result, index) => {
          const file = filesSnapshot[index];
          if (!file) return;

          const description = descriptionsSnapshot[index] || '';

          if (result.success && result.data && result.data.url) {
            const contentItem = {
              id: `content_${Date.now()}_${index}`,
              type: file.mediaType,
              url: getMediaUrl(result.data.url),
              caption: description,
            };
            onSaveContent(contentItem);
          } else {
            const contentItem = {
              id: `content_${Date.now()}_${index}`,
              type: file.mediaType,
              url: file.image,
              caption: description,
            };
            onSaveContent(contentItem);
          }
        });

        setIsAddCapsuleContentPopupOpen(false);
      } catch {
        alert('Ошибка при загрузке файлов. Попробуйте еще раз.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSaveDescription = () => {
    if (selectedFileIndex !== null) {
      const description = fileDescriptions[selectedFileIndex] || '';
      setFiles(prevFiles => {
        const newFiles = [...prevFiles];
        if (newFiles[selectedFileIndex]) {
          newFiles[selectedFileIndex] = {
            ...newFiles[selectedFileIndex],
            hasDescription: description.trim().length > 0,
          };
        }
        return newFiles;
      });
    }
    handleBackToUpload();
  };

  return createPortal(
    <div className={styles.overlay}>
      <div className={cn(styles.wrapper, { [styles.mobile]: isMobile })}>
        {isMobile && <div className={styles.handle}></div>}
        <div className={styles.content}>
          <input
            ref={replaceFileInputRef}
            type='file'
            style={{ display: 'none' }}
            onChange={handleReplaceFileSelect}
            accept='.jpg,.jpeg,.png,.gif,.webp,.mp3,.mp4,.mov'
          />
          {isViewMode ? (
            selectedFileIndex !== null && (
              <FileViewer
                selectedFile={files[selectedFileIndex]}
                currentDescription={fileDescriptions[selectedFileIndex] || ''}
                onBackToUpload={handleBackToUpload}
                onDescriptionChange={handleDescriptionChange}
                onDeleteDescription={handleDeleteDescription}
                onReplaceMedia={handleReplaceMedia}
                onSave={handleSaveDescription}
              />
            )
          ) : (
            <UploadView
              files={files.map((file, index) => ({
                ...file,
                hasDescription:
                  (fileDescriptions[index]?.trim().length ?? 0) > 0,
              }))}
              onDeleteFile={handleDeleteFile}
              onOpenFile={handleOpenFile}
              onFilesUploaded={handleFilesUploaded}
              onClosePopup={handleClosePopup}
              onClearAll={handleClearAll}
              onSave={handleSave}
              isUploading={isUploading}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
